/**
 * The work order's Description, filled in from the estimate it prices.
 *
 * Jonathon, 2026-10-17: "there needs to be a copy from estimate button on the
 * work order so that we don't have to type whatever we typed in the estimate
 * in the work order section for the sub contractor."
 *
 * What a rep types on an estimate is each line's name and the scope under it —
 * "1) Remove existing shingles down to the deck …". That is the same thing a
 * crew needs to read, so it comes across word for word, line by line, in the
 * builder's order. Nothing the customer pays is in it: the Description is
 * printed on the crew's copy, and a sub has no business with the customer's
 * price.
 *
 * Left behind, and why:
 *  - lines not being done (`work_doing` false) — they are not the job;
 *  - upgrades the customer did not buy. Signing writes each ticked upgrade
 *    onto the estimate as a line carrying its key in `upgrade_group`, so a line
 *    whose key is among the standing signature's `upgrade_groups` was bought
 *    and comes across like any other work. Any other upgrade line is an
 *    offer, and a crew told to fit an upgrade nobody bought fits it;
 *  - the proposal's own wording (What You Get, the warranty …) — that is sales
 *    copy, not work. The rep's own block for this house is kept, because it is
 *    the one piece of wording typed about this roof;
 *  - the protective clauses the proposal adds — they are said to the customer.
 *
 * And inside the name and scope that do come across, what was said to the customer is
 * taken out (`crewWording`). Jonathon, 2026-10-17: "it just needs to make sure
 * it doesn't say any customer facing verbiage in the subcontractor field.
 * meaning if the estimate line item says it'll be this much more etc that's not
 * something we'd want to pass on to the sub."
 */

import { isHomeSpecificSection, isWordingLine } from "./presentation.js";

export interface WorkOrderScopeLine {
  description?: string | null;
  note?: string | null;
  section?: string | null;
  work_doing?: boolean | null;
  customer_visible?: boolean | null;
  upgrade_group?: string | null;
  sort_order?: number | null;
}

/** One signature, as `proposal_acceptances` holds it. */
export interface WorkOrderSignature {
  estimate_id: string | null;
  extra_estimate_ids?: readonly string[] | null;
  upgrade_groups?: readonly string[] | null;
  /** The proposal it signed was voided: the record stays, the force goes. */
  voided: boolean;
}

/**
 * The upgrades the customer bought on one estimate: the `upgrade_groups` of
 * every signature that still stands and names it. A signature names the
 * estimate it was made on and every extra estimate it bought alongside
 * (ADR 0120), so an option signed as an extra brings its upgrades with it.
 */
export function boughtUpgrades(
  signatures: readonly WorkOrderSignature[],
  estimateId: string,
): string[] {
  return signatures
    .filter(
      (row) =>
        !row.voided &&
        (row.estimate_id === estimateId ||
          (row.extra_estimate_ids ?? []).includes(estimateId)),
    )
    .flatMap((row) => row.upgrade_groups ?? []);
}

/** Whether a line belongs in the crew's description. */
function forTheCrew(
  item: WorkOrderScopeLine,
  bought: ReadonlySet<string>,
): boolean {
  if (item.work_doing === false) return false;
  if (item.customer_visible === false) return false;
  const upgrade = (item.upgrade_group ?? "").trim();
  if (upgrade !== "" && !bought.has(upgrade)) return false;
  if (isWordingLine(item) && !isHomeSpecificSection(item.section)) return false;
  return (
    (item.description ?? "").trim() !== "" || (item.note ?? "").trim() !== ""
  );
}

/**
 * The estimate's work as text: each line's name, the scope typed under it, a
 * blank line between lines. Empty when the estimate has nothing to copy.
 *
 * `boughtUpgrades` is the `upgrade_groups` of the signatures that still stand
 * on this estimate: the upgrades the customer ticked.
 */
export function workOrderScopeFromEstimate(
  items: readonly WorkOrderScopeLine[],
  boughtUpgrades: readonly string[] = [],
): string {
  const bought = new Set(boughtUpgrades.map((key) => key.trim()));
  return (
    [...items]
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      .filter((item) => forTheCrew(item, bought))
      // The name is typed by the same rep as the scope, and a price lands in it
      // just as easily ("Extra decking @ $85/sheet"), so it is filtered the same
      // way. A line with nothing left of either is not copied at all.
      .map((item) =>
        [crewWording(item.description), crewWording(item.note)]
          .filter((part) => part !== "")
          .join("\n"),
      )
      .filter((block) => block !== "")
      .join("\n\n")
  );
}

/*
 * What a scope says to the customer rather than to whoever does the work.
 *
 * Read off the scopes on real estimates, which say it in a handful of ways:
 * what more will cost ("Additional plywood: $85/sheet"), what will need a
 * change order or a new quote, the warranty, and the sell ("giving you total
 * peace of mind", "see the colour pages"). What the crew is told *not* to do —
 * "This does not include any tear off" — is work, and stays.
 *
 * A pattern list, not understanding: a sentence worded some new way gets
 * through, and the Description is an ordinary box the rep can edit after.
 */

/** Pieces of a sentence that are said to the customer; the rest of it stays. */
const CUSTOMER_CLAUSES: readonly RegExp[] = [
  // "replace it at $20 per foot for fascia and $20 per foot for soffit"
  /,?\s*at \$\s?[\d,]+(?:\.\d+)?\s*(?:per|\/)\s*[\w-]+(?:\s+for\s+\w+)?(?:,?\s*and\s+\$\s?[\d,]+(?:\.\d+)?\s*(?:per|\/)\s*[\w-]+(?:\s+for\s+\w+)?)*/gi,
  /\s*\((?:included|this is only for [^)]*)\)/gi,
  /,?\s*ensuring a [^.,;]*warranty/gi,
  /,?\s*giving you [^.;]*/gi,
  /,?\s*in the (?:standard )?colou?r of (?:your|the customer'?s) choice/gi,
];

/** A sentence holding any of these is said to the customer, all of it. */
const CUSTOMER_SENTENCES: readonly RegExp[] = [
  /\$\s?\d/,
  /change order/i,
  /updated estimate|separate estimate|estimate will/i,
  /\bquot(?:e|ed|ing)\b/i,
  /additional cost|extra cost|at no (?:extra |additional )?(?:cost|charge)|will be charged/i,
  /peace of mind/i,
  /colou?r pages|your choice of colou?r/i,
  /not (?:be )?responsible for/i,
  /does not guarantee|no guarantee/i,
  /exact match/i,
  /no more getting on a ladder|instead of holding a garden/i,
  /may take up to [^.]* weeks/i,
  // A warranty the customer is promised: one with a length on it.
  /warrant[^.]*\b(?:years?|lifetime)\b|\b(?:years?|lifetime)\b[^.]*warrant/i,
];

/** "1.", "2:", "3A:", "2.1.", "4)", or a bullet — kept apart from the words. */
const PREFIX = /^\s*(?:[-•*]+\s*|\d+(?:\.\d+)*[A-Z]?[.:)]+\s*)?/;

/** A numbered heading: "6. Final Details", "3A: Exposed Fastener …". */
const NUMBERED = /^\s*\d+(?:\.\d+)*[A-Z]?[.:)]/;

/** A bulleted line: "-Install …", "• Install …", "* Install …" — not "**Bold**". */
const BULLET = /^\s*(?:[-•]|\*\s)/;

/** The verbs a line of work starts with: "Install …", "4) Replace …". */
const WORK_VERB =
  /^(?:add|apply|attach|build|caulk|check|clean|complete|cover|cut|fasten|fit|flash|follow|frame|haul|inspect|install|lay|measure|nail|paint|place|prep|provide|put|re-?install|remove|repair|replace|run|seal|secure|set|tear|use|wrap)\b/i;

/** "per GAF warranty specs", "to the guarantee spec": a warranty the work is done to. */
const WARRANTY_REFERENCE =
  /\b(?:per|to|for|with|under|meets?|according)\b[^:]*\b(?:warrant|guarantee)/i;

/**
 * A heading for a warranty section: "5: GAF Silver Pledge Warranty",
 * "Warranty and Guarantees", "Workmanship Warranty:".
 *
 * Taking a line of work for one drops every line after it, so a line that
 * reads as work never is one: a bullet ("-Install drip edge per manufacturer
 * warranty specs"), a line that starts with a work verb ("4) Install ridge
 * vent per warranty"), or one that names the warranty only as what the work is
 * done to ("Starter strip per GAF warranty specs").
 */
function isWarrantyHeading(line: string): boolean {
  if (BULLET.test(line)) return false;
  const words = line.replace(PREFIX, "").replace(/\*/g, "").trim();
  return (
    words.length <= 60 &&
    !/[.!?]$/.test(words) &&
    /warrant|guarantee/i.test(words) &&
    !WORK_VERB.test(words) &&
    !WARRANTY_REFERENCE.test(words)
  );
}

/** One line of a scope, with what is said to the customer taken out. */
function crewLine(line: string): string {
  const prefix = PREFIX.exec(line)?.[0] ?? "";
  let words = line.slice(prefix.length);
  for (const clause of CUSTOMER_CLAUSES) words = words.replace(clause, "");
  const kept = words
    .split(/(?<=[.!?])\s+/)
    .filter((sentence) => !CUSTOMER_SENTENCES.some((p) => p.test(sentence)))
    .join(" ")
    .trim();
  // Nothing of substance left: drop the line, bullet and all.
  if (!/[a-z0-9]/i.test(kept.replace(/\*+/g, ""))) return "";
  return prefix + kept;
}

/**
 * A scope as a crew should read it: the work, with the prices, the
 * change-order talk, the warranty and the sell taken out. A warranty section
 * goes from its heading to the next numbered heading, or to the end.
 */
export function crewWording(scope: string | null | undefined): string {
  const out: string[] = [];
  let inWarranty = false;
  for (const raw of (scope ?? "").replace(/\r\n?/g, "\n").split("\n")) {
    if (raw.trim() === "") {
      out.push("");
      continue;
    }
    if (isWarrantyHeading(raw)) {
      inWarranty = true;
      continue;
    }
    if (inWarranty) {
      if (!NUMBERED.test(raw)) continue;
      inWarranty = false;
    }
    const line = crewLine(raw);
    if (line !== "") out.push(line.trimEnd());
  }
  return out
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}
